import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PRODUCTS } from "@/data/products";

const ProductCard = ({ product, eager }) => {
  const images =
    product.images && product.images.length
      ? product.images
      : [product.image, product.hoverImage].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const total = images.length;
  const hasGallery = total > 1;

  const go = (delta) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((idx + delta + total) % total);
  };

  return (
    <article
      data-testid={`catalogue-product-${product.slug}`}
      className="group bg-white border border-stone-200 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-ink-900/5 transition-all duration-500 flex flex-col"
    >
      <div className="aspect-square overflow-hidden bg-white p-6 relative">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={product.name}
            loading={eager && i === 0 ? "eager" : "lazy"}
            className={`absolute inset-6 m-auto max-w-[calc(100%-3rem)] max-h-[calc(100%-3rem)] object-contain transition-opacity duration-500 ease-out ${
              i === idx ? "opacity-100" : "opacity-0"
            } ${i === idx ? "group-hover:scale-105" : ""} transition-transform`}
          />
        ))}
        {hasGallery && (
          <>
            <button
              type="button"
              onClick={go(-1)}
              data-testid={`gallery-prev-${product.slug}`}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md grid place-items-center text-ink-900 opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={go(1)}
              data-testid={`gallery-next-${product.slug}`}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md grid place-items-center text-ink-900 opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === idx ? "bg-ink-900" : "bg-stone-300"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-terracotta-600 mb-3">
          {product.category}
        </span>
        <h3 className="font-serif text-2xl text-ink-900 leading-tight">
          {product.name}
        </h3>
        <p className="mt-3 text-stone-700 leading-relaxed text-sm flex-1">
          {product.description}
        </p>
        <Link
          to={`/?inquiry=${encodeURIComponent(product.name)}#contact`}
          data-testid={`inquire-${product.slug}`}
          className="mt-5 inline-flex items-center gap-2 text-ink-900 text-sm font-medium border-b border-ink-900 pb-1 self-start hover:gap-4 transition-all"
        >
          Inquire <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
};

const ProductsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Products · SBK Manufacturing Co.";
  }, []);

  const categories = Array.from(new Set(PRODUCTS.map((p) => p.category)));

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar transparentOnTop={false} />

      {/* Page Hero */}
      <section
        data-testid="products-page-hero"
        className="pt-32 md:pt-40 pb-16 md:pb-20 border-b border-stone-200"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-ink-900/40" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
                What We Make
              </span>
            </div>
            <h1 className="font-serif font-light text-ink-900 text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
              Our complete <em className="italic text-terracotta-600 not-italic">catalogue</em> of
              notebooks &amp; paper goods.
            </h1>
            <p className="mt-7 text-lg text-stone-700 max-w-2xl leading-relaxed">
              From classroom essentials to corporate journals — every product below is
              manufactured in our Pune facility, customizable end to end, and export-ready.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end">
            {categories.map((c) => (
              <a
                key={c}
                href={`#${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                data-testid={`catalogue-cat-pill-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-stone-300 text-stone-700 rounded-sm hover:bg-ink-900 hover:text-stone-50 hover:border-ink-900 transition-colors"
              >
                {c}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid — grouped by category */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-20 md:space-y-24">
          {categories.map((cat, ci) => {
            const catProducts = PRODUCTS.filter((p) => p.category === cat);
            return (
              <div
                key={cat}
                data-testid={`catalogue-section-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                id={cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              >
                <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 pb-6 border-b border-stone-300">
                  <div className="flex items-end gap-5">
                    <span className="font-mono text-[12px] tracking-[0.3em] uppercase text-terracotta-600 leading-none">
                      0{ci + 1}
                    </span>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-700 block mb-2">
                        Category
                      </span>
                      <h2 className="font-serif font-light text-ink-900 text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                        {cat}
                      </h2>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-700 hidden md:block whitespace-nowrap">
                    {catProducts.length}{" "}
                    {catProducts.length === 1 ? "product" : "products"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                  {catProducts.map((p, i) => (
                    <ProductCard
                      key={p.slug}
                      product={p}
                      eager={ci === 0 && i < 4}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink-900 text-stone-50 py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif font-light text-stone-50 text-4xl md:text-5xl leading-tight tracking-tight">
            Don't see what you need?{" "}
            <em className="italic text-terracotta-500">We custom-make it.</em>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-stone-50/80 leading-relaxed">
            Private label, special bindings, custom paper weights, foiling, debossing — share
            your brief and we'll send a quote within one business day.
          </p>
          <Link
            to="/#contact"
            data-testid="catalogue-cta-contact"
            className="mt-10 inline-flex items-center gap-2 bg-stone-50 text-ink-900 px-8 py-4 rounded-sm font-medium hover:bg-stone-100 transition-colors"
          >
            Request a Custom Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
